import React, { useState, useEffect } from 'react';
import { AppIcon, documentCategoryIcon } from '../../icons/appIcons.tsx';
import EmptyState from '../common/EmptyState.tsx';

interface Document {
  id: string;
  title: string;
  description: string;
  fileName: string;
  originalFileName: string;
  fileSize: number;
  mimeType: string;
  category: 'flyer' | 'lesson_plan' | 'medical' | 'form' | 'announcement' | 'policy' | 'other';
  shareType: 'all_families' | 'specific_families' | 'admin_only';
  downloadCount: number;
  expiresAt: string | null;
  createdAt: string;
  uploader: {
    firstName: string;
    lastName: string;
  };
}

interface DocumentsViewProps {
  compact?: boolean;
}

export default function DocumentsView({ compact = false }: DocumentsViewProps) {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [downloading, setDownloading] = useState<string | null>(null);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/documents', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setDocuments(data.data);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (documentId: string, title: string) => {
    setDownloading(documentId);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/documents/${documentId}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        // Create a blob from the response
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        
        // Create a temporary link and click it to download
        const link = document.createElement('a');
        link.href = url;
        link.download = title;
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        const errorData = await response.json();
        alert('Download failed: ' + (errorData.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Failed to download document.');
    } finally {
      setDownloading(null);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const isExpired = (expiresAt: string | null) => {
    if (!expiresAt) return false;
    return new Date() > new Date(expiresAt);
  };

  const filteredDocuments = documents.filter(doc => {
    if (filter === 'all') return true;
    return doc.category === filter;
  });

  const categories = [...new Set(documents.map(doc => doc.category))];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ggaa-green"></div>
        <span className="ml-2 text-gray-600">Loading documents...</span>
      </div>
    );
  }

  if (compact) {
    // Compact view for dashboard
    const recentDocuments = documents.slice(0, 5);
    
    return (
      <div className="bg-white rounded-xl shadow-lg p-6 border border-ggaa-green/20">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Recent Documents</h3>
          <span className="text-sm text-gray-500">{documents.length} total</span>
        </div>
        
        {recentDocuments.length === 0 ? (
          <EmptyState icon="emptyDocs" title="No documents available" className="py-6" />
        ) : (
          <div className="space-y-3">
            {recentDocuments.map(document => (
              <div key={document.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center flex-1">
                  <AppIcon name={documentCategoryIcon(document.category)} className="w-5 h-5 mr-3 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">{document.title}</div>
                    <div className="text-xs text-gray-500">
                      {formatDate(document.createdAt)} • {formatFileSize(document.fileSize)}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleDownload(document.id, document.originalFileName)}
                  disabled={downloading === document.id || isExpired(document.expiresAt)}
                  className="ml-2 text-blue-600 hover:text-blue-800 text-sm font-medium disabled:opacity-50"
                >
                  {downloading === document.id ? '...' : 'Download'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Full view
  return (
    <div className="px-12 py-10">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Documents</h1>
        <p className="text-gray-600 mt-2">Download documents shared with your family</p>
      </div>

      {/* Filters */}
      {categories.length > 1 && (
        <div className="mb-6 flex flex-wrap gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'all' 
                ? 'bg-ggaa-green text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All ({documents.length})
          </button>
          {categories.map(category => {
            const count = documents.filter(d => d.category === category).length;
            return (
              <button
                key={category}
                onClick={() => setFilter(category)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filter === category 
                    ? 'bg-ggaa-green text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <span className="inline-flex items-center gap-1.5">
                  <AppIcon name={documentCategoryIcon(category)} className="w-4 h-4" />
                  {category.replace('_', ' ').charAt(0).toUpperCase() + category.replace('_', ' ').slice(1)} ({count})
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Documents Grid */}
      {filteredDocuments.length === 0 ? (
        <div className="bg-white rounded-xl shadow-lg">
          <EmptyState
            icon="emptyDocs"
            title="No documents available"
            description={
              filter === 'all'
                ? "No documents have been shared with your family yet."
                : `No ${filter.replace('_', ' ')} documents are available.`
            }
            className="py-12"
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDocuments.map(document => (
            <div key={document.id} className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-shadow">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <AppIcon name={documentCategoryIcon(document.category)} className="w-6 h-6 mr-3 shrink-0" />
                    <div>
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                        {document.category.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                  {isExpired(document.expiresAt) && (
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                      Expired
                    </span>
                  )}
                </div>
                
                <h3 className="text-lg font-semibold text-gray-900 mb-2 truncate" title={document.title}>
                  {document.title}
                </h3>
                
                {document.description && (
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2" title={document.description}>
                    {document.description}
                  </p>
                )}
                
                <div className="space-y-2 text-sm text-gray-500 mb-4">
                  <div className="flex justify-between">
                    <span>File:</span>
                    <span className="font-medium truncate ml-2" title={document.originalFileName}>
                      {document.originalFileName}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Size:</span>
                    <span>{formatFileSize(document.fileSize)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Uploaded:</span>
                    <span>{formatDate(document.createdAt)}</span>
                  </div>
                  {document.expiresAt && (
                    <div className="flex justify-between">
                      <span>Expires:</span>
                      <span className={isExpired(document.expiresAt) ? 'text-red-600 font-medium' : ''}>
                        {formatDate(document.expiresAt)}
                      </span>
                    </div>
                  )}
                </div>
                
                <button
                  onClick={() => handleDownload(document.id, document.originalFileName)}
                  disabled={downloading === document.id || isExpired(document.expiresAt)}
                  className="w-full bg-ggaa-green text-white py-2 px-4 rounded-lg hover:bg-ggaa-green/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  {downloading === document.id ? (
                    <>Downloading...</>
                  ) : isExpired(document.expiresAt) ? (
                    <>Expired</>
                  ) : (
                    <>Download</>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
} 